import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { searchApi } from '../api/searchApi';
import type {
  SearchRequest,
  SearchResponse,
  SearchSuggestResponse,
  RecentSearch,
  SavedSearch,
  SaveSearchRequest,
  UpdateSavedSearchRequest,
} from '../types';

// Query key factory
export const searchKeys = {
  all: ['search'] as const,
  results: () => [...searchKeys.all, 'results'] as const,
  result: (params: Omit<SearchRequest, 'offset'>) =>
    [...searchKeys.results(), params] as const,
  resultInfinite: (params: Omit<SearchRequest, 'offset'>) =>
    [...searchKeys.results(), 'infinite', params] as const,
  suggestions: () => [...searchKeys.all, 'suggestions'] as const,
  suggestion: (query: string) => [...searchKeys.suggestions(), query] as const,
  recent: () => [...searchKeys.all, 'recent'] as const,
  saved: () => [...searchKeys.all, 'saved'] as const,
  savedDetail: (id: string) => [...searchKeys.saved(), id] as const,
};

// Search hook
export function useSearch(
  params: Omit<SearchRequest, 'offset'>,
  options?: Omit<UseQueryOptions<SearchResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: searchKeys.result(params),
    queryFn: () =>
      searchApi.search({
        ...params,
        offset: 0,
        include_highlights: true,
        include_facets: true,
      }),
    enabled: !!params.query && params.query.length >= 2,
    ...options,
  });
}

// Infinite search for pagination
export function useInfiniteSearch(
  params: Omit<SearchRequest, 'offset'>,
  options?: { enabled?: boolean }
) {
  const limit = params.limit ?? 20;

  return useInfiniteQuery({
    queryKey: searchKeys.resultInfinite(params),
    queryFn: ({ pageParam = 0 }) =>
      searchApi.search({
        ...params,
        limit,
        offset: pageParam,
        include_highlights: true,
        include_facets: pageParam === 0, // Only get facets on first page
      }),
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.limit;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    initialPageParam: 0,
    enabled: (options?.enabled ?? true) && !!params.query && params.query.length >= 2,
  });
}

// Suggestions hook with debounce support
export function useSuggestions(
  query: string,
  options?: Omit<UseQueryOptions<SearchSuggestResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: searchKeys.suggestion(query),
    queryFn: () => searchApi.suggest(query, 10),
    enabled: query.length >= 2,
    staleTime: 30000, // 30 seconds
    ...options,
  });
}

// Recent searches hook
export function useRecentSearches(
  limit?: number,
  options?: Omit<UseQueryOptions<RecentSearch[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: searchKeys.recent(),
    queryFn: () => searchApi.getRecentSearches(limit),
    ...options,
  });
}

// Clear recent searches mutation
export function useClearRecentSearches(
  options?: UseMutationOptions<void, Error, void>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => searchApi.clearRecentSearches(),
    onSuccess: () => {
      queryClient.setQueryData(searchKeys.recent(), []);
    },
    ...options,
  });
}

// Saved searches hook
export function useSavedSearches(
  options?: Omit<UseQueryOptions<SavedSearch[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: searchKeys.saved(),
    queryFn: () => searchApi.listSavedSearches(),
    ...options,
  });
}

// Single saved search hook
export function useSavedSearch(
  id: string,
  options?: Omit<UseQueryOptions<SavedSearch>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: searchKeys.savedDetail(id),
    queryFn: () => searchApi.getSavedSearch(id),
    enabled: !!id,
    ...options,
  });
}

// Save search mutation
export function useSaveSearch(
  options?: UseMutationOptions<SavedSearch, Error, SaveSearchRequest>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SaveSearchRequest) => searchApi.saveSearch(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: searchKeys.saved() });
    },
    ...options,
  });
}

// Update saved search mutation
export function useUpdateSavedSearch(
  options?: UseMutationOptions<
    SavedSearch,
    Error,
    { id: string; request: UpdateSavedSearchRequest }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }) => searchApi.updateSavedSearch(id, request),
    onSuccess: (updatedSearch) => {
      queryClient.setQueryData(
        searchKeys.savedDetail(updatedSearch.id),
        updatedSearch
      );
      queryClient.invalidateQueries({ queryKey: searchKeys.saved() });
    },
    ...options,
  });
}

// Delete saved search mutation
export function useDeleteSavedSearch(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => searchApi.deleteSavedSearch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: searchKeys.saved() });
    },
    ...options,
  });
}

// Execute saved search mutation
export function useExecuteSavedSearch(
  options?: UseMutationOptions<SearchResponse, Error, string>
) {
  return useMutation({
    mutationFn: (id: string) => searchApi.executeSavedSearch(id),
    ...options,
  });
}

// Reindex mutations
export function useReindexWorkflow(
  options?: UseMutationOptions<void, Error, string>
) {
  return useMutation({
    mutationFn: (workflowId: string) => searchApi.reindexWorkflow(workflowId),
    ...options,
  });
}

export function useReindexAll(options?: UseMutationOptions<void, Error, void>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => searchApi.reindexAll(),
    onSuccess: () => {
      // Invalidate all search results after reindex
      queryClient.invalidateQueries({ queryKey: searchKeys.results() });
    },
    ...options,
  });
}
